// src/pages/labita/stok-lahan/StokLahan.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, push, update } from "firebase/database";

// Import XLSX untuk handling file Excel
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    excelDateToDDMMYYYY,
    formatNumber,
    formatDecimal,
    formatText,
    calculatePercentage
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

import TotalStokLahanNF from "../total-stok-lahan/TotalStokLahanNF";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Hitung % Real = Real / Luas Netto * 100
const calculatePercentageReal = (real, luasNetto) => {
    if (!real || !luasNetto || real === '-' || luasNetto === '-' || luasNetto === '') {
        return '-';
    }
    
    try {
        // Parse real value
        const realNum = parseFloat(real.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
        
        // Parse luas netto value
        const luasNettoNum = parseFloat(luasNetto.toString().replace(/[^\d.,]/g, '').replace(',', '.'));
        
        if (isNaN(realNum) || isNaN(luasNettoNum) || luasNettoNum === 0) {
            return '-';
        }
        
        const percentage = (realNum / luasNettoNum) * 100;
        
        // 1. Hilangkan angka di belakang koma
        // Jika hasilnya bilangan bulat (misal 100.0), tampilkan tanpa desimal
        const roundedPercentage = Math.round(percentage);
        
        // Cek apakah hasil rounding sama dengan nilai asli (toleransi 0.1)
        if (Math.abs(percentage - roundedPercentage) < 0.1) {
            return `${roundedPercentage}%`;
        } else {
            // Jika bukan bilangan bulat, tampilkan tanpa desimal dengan pembulatan
            return `${roundedPercentage}%`;
        }
    } catch (error) {
        console.error("Error calculating percentage:", error);
        return '-';
    }
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find(lokasi => lokasi.id_lokasi === id_lokasi);
};

// Fungsi untuk import Excel ke Firebase untuk stok lahan
const importExcelToFirebaseStokLahan = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1, 
                    raw: false,
                    defval: '' 
                });
                
                // Debug: log struktur data Excel
                console.log("Data Excel raw (10 baris pertama):");
                for (let i = 0; i < Math.min(10, jsonData.length); i++) {
                    console.log(`Row ${i}:`, jsonData[i]);
                }
                
                const dataStartRow = 2; // B5 (0-based index: 4)
                
                const columnMapping = {
                    id_lokasi: 1,        // Kolom C (index 2)
                    ex_comodity: 3,      // Kolom E (index 4)
                    real: 4,             // Kolom F (index 5)
                    interval: 5,         // Kolom G (index 6)
                    alokasi: 6,          // Kolom H (index 7)
                    aktifitas: 7         // Kolom I (index 8)
                };

                console.log("Sample raw data dari Excel (baris 5):");
                if (jsonData.length > dataStartRow) {
                    const sampleRow = jsonData[dataStartRow];
                    console.log("Row 5 raw:", sampleRow);
                    console.log("id_lokasi:", sampleRow[columnMapping.id_lokasi]);
                    console.log("ex_comodity:", sampleRow[columnMapping.ex_comodity]);
                    console.log("real:", sampleRow[columnMapping.real]);
                    console.log("alokasi:", sampleRow[columnMapping.alokasi]);
                }

                const stokLahanRef = ref(database, "db_labita/tb_stok_lahan");
                const existingStokLahanSnapshot = await onValuePromise(stokLahanRef);
                const existingStokLahanData = existingStokLahanSnapshot.val() || {};

                const existingStokLahanMap = new Map();
                Object.keys(existingStokLahanData).forEach(fbKey => {
                    const record = existingStokLahanData[fbKey];
                    if (record.id_lokasi) {
                        const key = record.id_lokasi.toString().trim();
                        existingStokLahanMap.set(key, fbKey);
                    }
                });

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;

                // Proses data
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const id_lokasi = row[columnMapping.id_lokasi];
                    const ex_comodity = row[columnMapping.ex_comodity];
                    const real = row[columnMapping.real];
                    const interval = row[columnMapping.interval];
                    const alokasi = row[columnMapping.alokasi];
                    const aktifitas = row[columnMapping.aktifitas];
                    
                    // 1. Skip jika id_lokasi kosong
                    if (!id_lokasi || (typeof id_lokasi === 'string' && id_lokasi.trim() === '')) {
                        console.log(`Baris ${i + 1} diabaikan: id_lokasi kosong`);
                        ignoredCount++;
                        continue;
                    }

                    const formattedIdLokasi = String(id_lokasi).trim();
                    const formattedExComodity = ex_comodity ? String(ex_comodity).trim() : '';
                    
                    // Format real dan interval sebagai text
                    let formattedReal, formattedInterval;
                    
                    if (real === undefined || real === null || 
                        (typeof real === 'string' && real.trim() === '')) {
                        formattedReal = '';
                    } else {
                        formattedReal = String(real).trim();
                    }
                    
                    if (interval === undefined || interval === null || 
                        (typeof interval === 'string' && interval.trim() === '')) {
                        formattedInterval = '';
                    } else {
                        formattedInterval = String(interval).trim();
                    }
                    
                    // Format alokasi
                    let formattedAlokasi;
                    if (alokasi === undefined || alokasi === null || 
                        (typeof alokasi === 'string' && alokasi.trim() === '')) {
                        formattedAlokasi = '';
                    } else {
                        formattedAlokasi = String(alokasi).trim();
                    }
                    
                    // Format aktifitas
                    let formattedAktifitas;
                    if (aktifitas === undefined || aktifitas === null || 
                        (typeof aktifitas === 'string' && aktifitas.trim() === '')) {
                        formattedAktifitas = '';
                    } else {
                        formattedAktifitas = String(aktifitas).trim();
                    }
                    
                    // Debug log untuk beberapa data pertama
                    if (importedCount + updatedCount < 2) {
                        console.log(`Data baris ${i + 1}:`);
                        console.log(`  id_lokasi: ${formattedIdLokasi}`);
                        console.log(`  ex_comodity: ${formattedExComodity}`);
                        console.log(`  real: ${formattedReal}`);
                        console.log(`  interval: ${formattedInterval}`);
                        console.log(`  alokasi: ${formattedAlokasi}`);
                        console.log(`  aktifitas: ${formattedAktifitas}`);
                    }

                    // Data dari Excel yang akan disimpan di Firebase
                    const excelDataToUpdate = {
                        id_lokasi: formattedIdLokasi,
                        ex_comodity: formattedExComodity,
                        real: formattedReal,
                        interval: formattedInterval,
                        alokasi: formattedAlokasi,
                        aktifitas: formattedAktifitas,
                        last_imported_at: importTimestamp,
                    };

                    const firebaseKey = existingStokLahanMap.get(formattedIdLokasi);

                    try {
                        if (firebaseKey) {
                            // Update data yang sudah ada
                            await update(ref(database, `db_labita/tb_stok_lahan/${firebaseKey}`), excelDataToUpdate);
                            updatedCount++;
                        } else {
                            // Buat data baru
                            await push(stokLahanRef, excelDataToUpdate);
                            importedCount++;
                        }
                        
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        ignoredCount++;
                    }
                }

                console.log(`Hasil import: ${importedCount} baru, ${updatedCount} update, ${ignoredCount} abaikan`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    ignoredCount,
                    total: importedCount + updatedCount
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

// Fungsi untuk refresh data setelah import
const refreshStokLahanData = async (database, onValuePromise) => {
    const stokLahanRef = ref(database, "db_labita/tb_stok_lahan");
    const updatedStokLahanSnapshot = await onValuePromise(stokLahanRef);
    const updatedStokLahanData = updatedStokLahanSnapshot.val() || {};
    
    const updatedLoadedData = [];
    Object.keys(updatedStokLahanData).forEach(key => {
        const record = updatedStokLahanData[key];
        updatedLoadedData.push({ 
            firebaseKey: key, 
            id_lokasi: record.id_lokasi || '',
            ex_comodity: record.ex_comodity || '',
            real: record.real || '',
            interval: record.interval || '',
            alokasi: record.alokasi || '',
            aktifitas: record.aktifitas || '',
        });
    });
    
    return updatedLoadedData;
};

function StokLahan() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk data konfigurasi dropdown
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [lokasiData, setLokasiData] = useState([]); // Array data dari db_freshpine/tb_lokasi
    
    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterExComodity, setFilterExComodity] = useState('');
    const [filterAktifitas, setFilterAktifitas] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_labita/tb_stok_lahan
                const stokLahanRef = ref(database, "db_labita/tb_stok_lahan");
                const stokLahanSnapshot = await onValuePromise(stokLahanRef);
                const stokLahanData = stokLahanSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (stokLahanData) {
                    Object.keys(stokLahanData).forEach(key => {
                        const record = stokLahanData[key];
                        loadedData.push({ 
                            firebaseKey: key, 
                            id_lokasi: record.id_lokasi || '',
                            ex_comodity: record.ex_comodity || '',
                            real: record.real || '',
                            interval: record.interval || '',
                            alokasi: record.alokasi || '',
                            aktifitas: record.aktifitas || '',
                        });
                        
                        // Cari waktu update terakhir dari last_imported_at
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Fetch data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();
                
                let lokasiArray = [];
                if (lokasiRawData) {
                    // Konversi dari object Firebase ke array
                    lokasiArray = Object.keys(lokasiRawData).map(key => ({
                        firebaseKey: key,
                        ...lokasiRawData[key]
                    }));
                    setLokasiData(lokasiArray);
                    
                    // Update dataList dengan informasi dari tb_lokasi
                    const updatedDataList = loadedData.map((item) => {
                        const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);
                        
                        // 3. Hitung persentase real = Real / Luas Netto
                        const luasNetto = lokasiInfo ? lokasiInfo.netto : '-';
                        const percentageReal = calculatePercentageReal(item.real, luasNetto);
                        
                        if (lokasiInfo) {
                            // Jika ditemukan di tb_lokasi
                            const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                            // 2. Luas aktif diganti dengan luas netto
                            const displayLuasNetto = lokasiInfo.netto || '-';
                            const displayStatus = lokasiInfo.status || '';
                            
                            return {
                                ...item,
                                lokasi: displayLokasi,
                                // 2. Field diubah dari luas_aktif menjadi luas_netto
                                luas_netto: displayLuasNetto,
                                status: displayStatus,
                                tahun: lokasiInfo.tahun || '',
                                // 3. Persentase real = Real / Luas Netto
                                percentage_real: percentageReal,
                            };
                        } else {
                            // Jika TIDAK ditemukan di tb_lokasi
                            return {
                                ...item,
                                lokasi: '-',
                                // 2. Field diubah dari luas_aktif menjadi luas_netto
                                luas_netto: '-',
                                status: '',
                                tahun: '',
                                // 3. Persentase real = Real / Luas Netto
                                percentage_real: percentageReal,
                            };
                        }
                    });
                    
                    setOriginalDataList(updatedDataList);
                    setDataList(updatedDataList);
                } else {
                    // Jika tb_lokasi kosong, gunakan data dasar
                    const basicDataList = loadedData.map((item) => {
                        // 3. Persentase real = Real / Luas Netto (dalam hal ini luas netto tidak ada)
                        const percentageReal = calculatePercentageReal(item.real, '');
                        
                        return {
                            ...item,
                            lokasi: item.id_lokasi || '',
                            // 2. Field diubah dari luas_aktif menjadi luas_netto
                            luas_netto: '-',
                            status: '',
                            tahun: '',
                            // 3. Persentase real = Real / Luas Netto
                            percentage_real: percentageReal
                        };
                    });
                    
                    setOriginalDataList(basicDataList);
                    setDataList(basicDataList);
                    setLokasiData([]);
                }

                // Fetch lookup data for Status Lokasi (untuk warna indicator)
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.lokasi && item.lokasi.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            const updatedData = await refreshStokLahanData(database, onValuePromise);
            
            // Update dengan data dari tb_lokasi jika tersedia
            const finalData = updatedData.map((item) => {
                const lokasiInfo = findLokasiById(lokasiData, item.id_lokasi);
                
                // 3. Hitung persentase real = Real / Luas Netto
                const luasNetto = lokasiInfo ? lokasiInfo.netto : '-';
                const percentageReal = calculatePercentageReal(item.real, luasNetto);
                
                if (lokasiInfo) {
                    const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                    // 2. Luas aktif diganti dengan luas netto
                    const displayLuasNetto = lokasiInfo.netto || '-';
                    const displayStatus = lokasiInfo.status || '';
                    
                    return {
                        ...item,
                        lokasi: displayLokasi,
                        // 2. Field diubah dari luas_aktif menjadi luas_netto
                        luas_netto: displayLuasNetto,
                        status: displayStatus,
                        tahun: lokasiInfo.tahun || '',
                        // 3. Persentase real = Real / Luas Netto
                        percentage_real: percentageReal,
                    };
                } else {
                    return {
                        ...item,
                        lokasi: '-',
                        // 2. Field diubah dari luas_aktif menjadi luas_netto
                        luas_netto: '-',
                        status: '',
                        tahun: '',
                        // 3. Persentase real = Real / Luas Netto
                        percentage_real: percentageReal,
                    };
                }
            });
            
            return finalData;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import khusus untuk stok lahan
            const result = await importExcelToFirebaseStokLahan(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui.`,
                    severity: "success",
                });
                
                // Set last updated time
                setLastUpdated(new Date());
                
                // Refresh data setelah import berhasil
                const finalData = await refreshDataAfterImport();
                
                setDataList(finalData);
                setOriginalDataList(finalData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterStatusLokasi) {
            filtered = filtered.filter(item => item.status === filterStatusLokasi);
        }
        if (filterExComodity) {
            filtered = filtered.filter(item => item.ex_comodity === filterExComodity);
        }
        if (filterAktifitas) {
            filtered = filtered.filter(item => item.aktifitas === filterAktifitas);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterExComodity('');
        setFilterAktifitas('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Get unique ex_comodity for filter dropdown
    const exComodityList = [...new Set(originalDataList.map(item => item.ex_comodity))].filter(Boolean).sort();
    
    // Get unique aktifitas for filter dropdown
    const aktifitasList = [...new Set(originalDataList.map(item => item.aktifitas))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/*<TotalStokLahanNF/>*/}

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Ex-Comodity</InputLabel>
                            <Select
                                value={filterExComodity}
                                label="Ex-Comodity"
                                onChange={(e) => setFilterExComodity(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {exComodityList.map((exComodity) => (
                                    <MenuItem key={exComodity} value={exComodity}>
                                        {exComodity}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Aktifitas</InputLabel>
                            <Select
                                value={filterAktifitas}
                                label="Aktifitas"
                                onChange={(e) => setFilterAktifitas(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {aktifitasList.map((aktifitas) => (
                                    <MenuItem key={aktifitas} value={aktifitas}>
                                        {aktifitas}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Stok Lahan
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari berdasarkan lokasi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="stok lahan table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch">Aktifitas</TableCell>
                                        <TableCell className="ded-tch">Lokasi</TableCell>
                                        <TableCell className="ded-tch">Ex-Comodity</TableCell>
                                        <TableCell className="ded-tch">Alokasi</TableCell>
                                        <TableCell className="ded-tch">Luas<br/>Netto</TableCell>
                                        <TableCell className="ded-tch">Real</TableCell>
                                        <TableCell className="ded-tch">% Real</TableCell>
                                        <TableCell className="ded-tch">Interval</TableCell>
                                        <TableCell className="ded-tch">Status<br/>Lokasi</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            // Lookup nama_status_lokasi dan indicator dari config
                                            const statusLokasiItem = statusLokasiList.find(
                                                (item) => item.nama_status_lokasi === row.status
                                            );
                                            const displayStatusLokasi = row.status || '';
                                            const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                                            return (
                                                <TableRow key={row.firebaseKey}>
                                                    {/*Aktifitas*/}
                                                    <TableCell>{formatText(row.aktifitas)}</TableCell>

                                                    {/* Lokasi */}
                                                    <TableCell>
                                                        <Box>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                {formatText(row.lokasi)}
                                                            </div>
                                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                                ID: {row.id_lokasi || '-'}
                                                            </div>
                                                        </Box>
                                                    </TableCell>

                                                    {/* Ex-Comodity */}
                                                    <TableCell>{formatText(row.ex_comodity)}</TableCell>

                                                    {/* Alokasi */}
                                                    <TableCell>{formatText(row.alokasi)}</TableCell>

                                                    {/* Luas Netto */}
                                                    <TableCell>{formatText(row.luas_netto)}</TableCell>

                                                    {/* Real */}
                                                    <TableCell>{formatText(row.real)}</TableCell>

                                                    {/* 3. % Real (Real / Luas Netto) - Tanpa angka di belakang koma */}
                                                    <TableCell>{formatText(row.percentage_real)}</TableCell>

                                                    {/*Interval*/}
                                                    <TableCell>{formatText(row.interval)}</TableCell>
                                                    
                                                    {/* Status Lokasi */}
                                                    <TableCell>
                                                        {displayStatusLokasi ? (
                                                            <Chip
                                                                label={displayStatusLokasi}
                                                                style={{ 
                                                                    backgroundColor: statusIndicatorColor, 
                                                                    color: theme.palette.getContrastText(statusIndicatorColor) 
                                                                }}
                                                                size="small"
                                                            />
                                                        ) : '-'}
                                                    </TableCell>
                                                    
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data stok lahan.'}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default StokLahan;